import React, { useState } from 'react';
import './register-screen.scss';
import { register } from '../../utils/api';
import { saveSession } from '../../utils/session';

const AR = {
  validation: {
    fullNameRequired: 'الاسم الكامل مطلوب',
    fullNameMinLength: 'الاسم الكامل يجب أن يكون حرفين على الأقل',
    mobileRequired: 'رقم الموبايل مطلوب',
    mobileInvalid: 'يرجى إدخال رقم موبايل صحيح',
    passwordRequired: 'كلمة المرور مطلوبة',
    passwordMinLength: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
    confirmPasswordRequired: 'تأكيد كلمة المرور مطلوب',
    passwordsMismatch: 'كلمة المرور وتأكيدها غير متطابقين',
  },
  errors: {
    registerFailed: 'فشل إنشاء الحساب. يرجى المحاولة مرة أخرى.',
    mobileExists: 'رقم الموبايل مسجل بالفعل. يرجى تسجيل الدخول.',
    unexpected: 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.',
    network: 'خطأ في الاتصال. تحقق من الاتصال بالإنترنت.',
  },
};

interface RegisterScreenProps {
  onComplete: (userData: { username: string; fullName: string; mobileNumber: string }) => void;
  onGoToLogin: () => void;
}

const RegisterScreen: React.FC<RegisterScreenProps> = ({ onComplete, onGoToLogin }) => {
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    fullName?: string;
    mobile?: string;
    password?: string;
    confirmPassword?: string;
    general?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!fullName.trim()) {
      newErrors.fullName = AR.validation.fullNameRequired;
    } else if (fullName.trim().length < 2) {
      newErrors.fullName = AR.validation.fullNameMinLength;
    }

    if (!mobile.trim()) {
      newErrors.mobile = AR.validation.mobileRequired;
    } else if (!/^\+?[\d\s-()]{8,15}$/.test(mobile.trim())) {
      newErrors.mobile = AR.validation.mobileInvalid;
    }

    if (!password) {
      newErrors.password = AR.validation.passwordRequired;
    } else if (password.length < 6) {
      newErrors.password = AR.validation.passwordMinLength;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = AR.validation.confirmPasswordRequired;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = AR.validation.passwordsMismatch;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getArabicErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      const msg = error.message.toLowerCase();
      if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('networkerror')) {
        return AR.errors.network;
      }
      if (msg.includes('مسجل') || msg.includes('already') || msg.includes('exists') || msg.includes('duplicate')) {
        return AR.errors.mobileExists;
      }
      if (error.message && error.message.length > 0 && /[\u0600-\u06FF]/.test(error.message)) {
        return error.message;
      }
      return AR.errors.registerFailed;
    }
    return AR.errors.unexpected;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const response = await register(
        {
          fullName: fullName.trim(),
          mobile: mobile.trim(),
          password,
          confirmPassword,
        },
        { skipGlobalLoading: true },
      );

      saveSession({
        token: response.token,
        userName: response.userName,
        fullName: response.fullName || '',
        mobile: response.mobile,
        email: response.email,
      });

      onComplete({
        username: response.userName,
        fullName: response.fullName || '',
        mobileNumber: response.mobile,
      });
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: getArabicErrorMessage(error) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-screen">
      <div className="register-screen__glow register-screen__glow--tr" aria-hidden />
      <div className="register-screen__glow register-screen__glow--bl" aria-hidden />
      <div className="register-screen__inner">
        <div className="register-header">
          <div className="register-header__logo-wrap">
            <img src="/new/blue.png" alt="Ameen" className="register-logo" />
          </div>
          <h1>إنشاء حساب جديد</h1>
          <p className="register-subtitle">أنشئ حسابك للبدء مع أمين</p>
        </div>

        <form
          className="auth-form-card register-form"
          onSubmit={handleSubmit}
          autoComplete="off"
          name="ameen-register"
        >
          {errors.general && (
            <div className="error-message general-error register-form__full" dir="rtl" role="alert">
              {errors.general}
            </div>
          )}

          <div className="register-form__row">
            <div className="form-group">
              <label htmlFor="fullName" className="form-label">
                الاسم الكامل
              </label>
              <input
                type="text"
                id="fullName"
                name="ameen-reg-fullname"
                className={`form-input ${errors.fullName ? 'error' : ''}`}
                value={fullName}
                onChange={e => {
                  setFullName(e.target.value);
                  if (errors.fullName) setErrors(prev => ({ ...prev, fullName: undefined }));
                }}
                placeholder="أدخل اسمك الكامل"
                disabled={isLoading}
                dir="rtl"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {errors.fullName && (
                <span className="error-message" dir="rtl">{errors.fullName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="registerMobile" className="form-label">
                رقم الموبايل
              </label>
              <input
                type="tel"
                id="registerMobile"
                name="ameen-reg-mobile"
                className={`form-input ${errors.mobile ? 'error' : ''}`}
                value={mobile}
                onChange={e => {
                  setMobile(e.target.value);
                  if (errors.mobile) setErrors(prev => ({ ...prev, mobile: undefined }));
                }}
                placeholder="أدخل رقم الموبايل"
                disabled={isLoading}
                autoComplete="off"
                autoCorrect="off"
              />
              {errors.mobile && (
                <span className="error-message" dir="rtl">{errors.mobile}</span>
              )}
            </div>
          </div>

          <div className="register-form__row">
            <div className="form-group">
              <label htmlFor="registerPassword" className="form-label">
                كلمة المرور
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="registerPassword"
                  name="ameen-reg-password"
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
                    if (errors.confirmPassword && e.target.value === confirmPassword) {
                      setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                    }
                  }}
                  placeholder="أدخل كلمة المرور"
                  aria-describedby="register-password-hint"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(prev => !prev)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                >
                  <span className="material-symbols-outlined">
                    {showPassword ? 'visibility' : 'visibility_off'}
                  </span>
                </button>
              </div>
              <p id="register-password-hint" className="password-field-hint" dir="rtl">
                يجب ان تكون 6 أحرف على الأقل
              </p>
              {errors.password && (
                <span className="error-message" dir="rtl">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                تأكيد كلمة المرور
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="ameen-reg-confirm-password"
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  value={confirmPassword}
                  onChange={e => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                  }}
                  placeholder="أعد إدخال كلمة المرور"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                >
                  <span className="material-symbols-outlined">
                    {showConfirmPassword ? 'visibility' : 'visibility_off'}
                  </span>
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message" dir="rtl">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <button type="submit" className="auth-form-card__submit" disabled={isLoading}>
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner" />
                جاري إنشاء الحساب...
              </span>
            ) : (
              'إنشاء الحساب'
            )}
          </button>
        </form>

        <div className="auth-page-footer" dir="rtl">
          <div className="auth-page-footer__row">
            <span className="auth-page-footer__line" aria-hidden />
            <span className="auth-page-footer__hint">لديك حساب بالفعل؟</span>
            <span className="auth-page-footer__line" aria-hidden />
          </div>
          <button
            type="button"
            className="auth-page-footer__cta"
            onClick={onGoToLogin}
            disabled={isLoading}
          >
            تسجيل الدخول
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;
